import React, { useState, useEffect, useContext, useMemo, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { useApolloClient, useLazyQuery, useMutation } from '@apollo/react-hooks'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useDropzone } from 'react-dropzone'
import { init as filestackInit } from 'filestack-js'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import _get from 'lodash/get'
import _orderBy from 'lodash/orderBy'

import Button from '@material-ui/core/Button'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import CardActions from '@material-ui/core/CardActions'
import CircularProgress from '@material-ui/core/CircularProgress'
import Grid from '@material-ui/core/Grid'
import Box from '@material-ui/core/Box'
import LinearProgress from '@material-ui/core/LinearProgress'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import Typography from '@material-ui/core/Typography'
import { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import PagAutocomplete from '@/components/pag-form/pag-autocomplete'
import { safeLabel } from '@/utils/option-label'
import PagTextInput from '@/components/pag-form/pag-text-input'
import PagCircularIndeterminate from '@/components/pag-loading'

import { useAuth0 } from '@/auth/index'
import useMounted from '@/components/hooks/use-mounted'
import { Context } from '@/shared/store/reducers/global-reducer'
import { errorHandler } from '@/shared/error-handler'
import {
  GET_AGENCIES,
  GET_AGENCY_PRODUCERS,
  GET_BID_TYPES,
  GET_CAMPAIGNS,
  GET_MARKETS,
  GET_PROJECT_CATEGORIES,
  GET_PROJECT_REGIONS,
  GET_PROJECT_STATUSES,
  GET_PROJECT_TYPES,
} from '@/shared/graphql'
import { ADD_PROJECT, UPDATE_PROJECT, UPLOAD_PROJECT_WITH_DOCUMENTS } from '@/shared/graphql/mutations/mutationProjects'
import { callPagApi } from '@/shared/services/pag-api'
import { getFilestackApiKey, getFilestackMaxFileSizeBytes } from '@/config/filestack.config'
import { validateFilestackFile } from '@/utils/file-utils'
import { DefaultFileIcon, FileIcons } from '@/shared/constants/file-icons'
import { IAgency } from '@/shared/models/agency'
import { IBrand } from '@/shared/models/brand'
import { IProject } from '@/shared/models/project'
import { IUploadProjectWithFiles } from '@/shared/models/upload-project-with-files'

import UploadPortalSearchProjectForm from '@/routes/upload-portal/components/search-project-form'
import UploadPortalNewProjectForm from '@/routes/upload-portal/components/new-project-form'
import UploadPortalUploadFilesForm from '@/routes/upload-portal/components/upload-files-form'

import { WidgetConfigType, WidgetConfigDefaultProps } from '../config'
import { NewProjectSchema } from './config'
import {
  getUploadPortalSelectFieldsToHydrate,
  injectUploadPortalSelectOptions,
  orderAgencies,
  orderAgencyProducers,
  orderBidTypes,
  orderCampaigns,
  orderMarkets,
  orderProjectCategories,
  orderProjectRegions,
  orderProjectStatuses,
  orderProjectTypes,
  UploadPortalSelectHydration,
} from './upload-portal-select-hydration'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'absolute',
    marginTop: 12,
    minWidth: 320,
    maxWidth: 420,
    boxShadow:
      '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
    zIndex: 1001,
    overflow: 'visible',
  },
  form: {
    display: 'block',
  },
  cardContent: {
    position: 'relative',
    padding: theme.spacing(2),
    paddingBottom: 0,
  },
  cardAction: {
    padding: theme.spacing(2),
    justifyContent: 'flex-end',
  },
  button: {
    minWidth: 100,
  },
  circularProgress: {
    position: 'absolute',
  },
  dropZoneWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgb(238, 238, 238)',
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: theme.palette.primary.main,
    fontSize: 12,
    textAlign: 'center',
    width: '100%',
    minHeight: 80,
    padding: theme.spacing(1),
    cursor: 'pointer',
    '&:hover': {
      background: 'rgb(248, 248, 248)',
    },
  },
  fileList: {
    maxHeight: 150,
    overflow: 'auto',
    marginTop: theme.spacing(1),
  },
  /** Portaled to document.body — fixed under header so MUI Autocomplete poppers are not trapped by AppBar/Toolbar */
  uploadProjectCard: {
    position: 'fixed',
    top: 88,
    right: 16,
    left: 'auto',
    marginTop: 0,
    minWidth: 360,
    width: 'min(560px, calc(100vw - 24px))',
    maxWidth: 'min(560px, calc(100vw - 24px))',
    maxHeight: 'calc(100vh - 96px)',
    overflowY: 'auto',
    overflowX: 'hidden',
    boxShadow:
      '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
    zIndex: 1500,
  },
  uploadProjectCardContent: {
    position: 'relative',
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    overflow: 'visible',
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  },
}))

const ProjectEditWidget = (props: any) => {
  const {
    widgetConfig: { order, area, permissions, style },
    config,
    inputs: { client, brand, project },
    outputs: { handleCancel, handleSubmit: onHandleSubmit },
  } = props
  const classes = useStyles(props)
  const mountedRef = useMounted(true)
  const { getTokenSilently } = useAuth0()
  const { store } = useContext(Context)
  const { activeTenant, clients } = store

  const hookFormMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      clientName: client?.name || '',
      brandId: brand?.id || 0,
      agencyId: 0,
      projectName: '',
      projectNumber: '',
    },
    resolver: yupResolver(NewProjectSchema),
  })

  const {
    control,
    setValue,
    getValues,
    formState: { errors, isValid },
    handleSubmit,
  } = hookFormMethods

  const [agencyOptions, setAgencyOptions] = useState([] as any)
  const [getAgencies, getAgenciesRes] = useLazyQuery(GET_AGENCIES, {
    onError: (err) => {
      if (!mountedRef.current) return
      setAgencyOptions([])
      errorHandler(err)
    },
    onCompleted: (res) => {
      if (Boolean(mountedRef.current)) {
        setAgencyOptions(_orderBy(res.agencies?.data || [], ['name']))
      }
    },
    fetchPolicy: 'network-only',
  })

  const [addProject, addProjectRes] = useMutation(ADD_PROJECT, {
    onError: (err) => {
      errorHandler(err)
    },
    onCompleted: (res) => {
      if (mountedRef.current && res.addProject?.id) {
        onHandleSubmit(res.addProject)
      }
    },
  })

  const [updateProject, updateProjectRes] = useMutation(UPDATE_PROJECT, {
    onError: (err) => {
      errorHandler(err)
    },
    onCompleted: (res) => { },
  })

  const [uploadProjectWithDocuments, uploadProjectWithDocumentsRes] = useMutation(
    UPLOAD_PROJECT_WITH_DOCUMENTS,
    {
      onError: (err) => {
        errorHandler(err)
      },
      onCompleted: (res) => {
        if (mountedRef.current && res.uploadProjectWithDocuments?.id) {
          onHandleSubmit(res.uploadProjectWithDocuments)
        }
      },
    }
  )

  // File upload state
  const uploadedFilesRef = useRef<{ filename: string; mimetype: string; url: string }[]>([])
  const uploadingFilesRef = useRef({})
  const uploadingProgressRef = useRef({
    uploading: false,
    value: 0,
  })
  const [forceUpdate, setForceUpdate] = useState(0)
  const [showFileUpload, setShowFileUpload] = useState(false)

  useEffect(() => {
    if (!activeTenant?.id) return
    const abortController = new AbortController()
    const { signal } = abortController
    callPagApi('', getTokenSilently, getAgencies, { tenantId: activeTenant.id }, signal) // GET Agencies

    return () => {
      if (abortController) abortController.abort()
    }
  }, [activeTenant?.id])

  const brandOptions = useMemo(() => {
    let options = []
    if (client) {
      options = client?.brands || []
    } else if (clients) {
      options = _get(clients, [0, 'brands']) || []
      setValue('clientName', _get(clients, [0, 'name']) || '')
    }

    return options
  }, [client, clients])

  // File upload handler
  const handleDropFiles = useCallback(
    (acceptedFiles: any) => {
      const fileStackClient = filestackInit(getFilestackApiKey())
      const selectedClient = client || _get(clients, [0])
      const selectedBrand = brandOptions.find((b: IBrand) => b.id === hookFormMethods.getValues('brandId'))

      const tags = {
        'Project Number': (getValues('projectNumber') || '').trim(),
        'Project Name': (getValues('projectName') || '').trim(),
        Tenant: activeTenant?.name || '',
        Client: selectedClient?.name || '',
        Brand: selectedBrand?.name || '',
      }
      const promises = []
      const tempProgress: number[] = []
      const numOfFiles = acceptedFiles.length
      uploadingProgressRef.current.uploading = true

      for (let i = 0; i < numOfFiles; i++) {
        // CHG-C1: validate file type before uploading (defense-in-depth, F4)
        const typeCheck = validateFilestackFile(acceptedFiles[i])
        if (!typeCheck.allowed) {
          toast.error(typeCheck.reason, { autoClose: 5000 })
          // eslint-disable-next-line no-continue
          continue
        }
        if (acceptedFiles[i].size > getFilestackMaxFileSizeBytes()) {
          toast.error(
            `You can't upload the file "${acceptedFiles[i].name}" that exceeds the maximum upload size(250MB).`,
            {
              autoClose: 5000,
            }
          )
        } else {
          promises.push(
            new Promise((resolve) => {
              fileStackClient
                .upload(
                  acceptedFiles[i],
                  {
                    onProgress: (evt: any) => {
                      tempProgress[i] = evt.totalPercent / numOfFiles
                      uploadingProgressRef.current = {
                        uploading: true,
                        value: tempProgress.reduce(
                          (prevValue, currentValue) => prevValue + currentValue,
                          0
                        ),
                      }
                      setForceUpdate(new Date().getTime())
                    },
                    tags,
                  },
                  {
                    filename: acceptedFiles[i].name.replace(/[<>:?/|\\"]/g, '-'),
                  },
                  uploadingFilesRef.current
                )
                .then((res) => {
                  if (res.status === 'Stored') {
                    uploadedFilesRef.current.push({
                      filename: res.filename,
                      mimetype: res.mimetype,
                      url: res.url,
                    })
                    setForceUpdate(new Date().getTime())
                  } else {
                    toast.error(
                      `Sorry, Uploading the file "${res.filename}" has been failed. Please ask Admin about this problem. ${res.status}`,
                      {
                        autoClose: 5000,
                      }
                    )
                  }
                })
                .catch((err) => {
                  toast.error(
                    `Sorry, Uploading the file "${acceptedFiles[i].name}" has been failed. Please ask Admin about this problem.`,
                    {
                      autoClose: 5000,
                    }
                  )
                })
                .finally(() => {
                  tempProgress[i] = 100 / numOfFiles
                  uploadingProgressRef.current = {
                    uploading: true,
                    value: tempProgress.reduce(
                      (prevValue, currentValue) => prevValue + currentValue,
                      0
                    ),
                  }
                  setForceUpdate(new Date().getTime())
                  resolve(true)
                })
            })
          )
        }
      }

      Promise.all(promises).then(() => {
        uploadingProgressRef.current = {
          uploading: false,
          value: 0,
        }
        setForceUpdate(new Date().getTime())
      })
    },
    [client, clients, brandOptions, activeTenant, getValues]
  )

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop: handleDropFiles,
    noClick: false,
    noKeyboard: true,
  })

  // Save Project
  const onSubmit = (data: any) => {
    let abortController: any

    try {
      abortController = new AbortController()
      const { signal } = abortController

      const selectedClient = client || _get(clients, [0])

      // If files are uploaded, use uploadProjectWithDocuments to trigger automations
      if (uploadedFilesRef.current.length > 0) {
        const payload: IUploadProjectWithFiles = {
          tenantId: activeTenant?.id,
          id: project?.id,
          brandId: data.brandId,
          agencyId: data.agencyId,
          clientId: selectedClient?.id,
          projectName: (data.projectName || '').trim(),
          projectNumber: (data.projectNumber || '').trim(),
          documents: uploadedFilesRef.current,
        }

        callPagApi('', getTokenSilently, uploadProjectWithDocuments, payload, signal)
      } else {
        // No files, use regular add/update project
        const payload: any = {
          tenantId: activeTenant?.id,
          brandId: data.brandId,
          agencyId: data.agencyId,
          projectName: (data.projectName || '').trim(),
          projectNumber: (data.projectNumber || '').trim(),
        }

        if (project) {
          payload['id'] = project.id
          callPagApi('', getTokenSilently, updateProject, payload, signal)
        } else {
          callPagApi('', getTokenSilently, addProject, payload, signal)
        }
      }
    } catch (err) {
      console.error('Error in Add Project', err)
      if (abortController) abortController.abort()
    }
  }

  return (
    <Card
      id="new-project-widget"
      className={clsx(classes.root)}
      style={style}
      elevation={24}
      variant="outlined"
    >
      <form name="newProjectWidgetForm" className={classes.form} onSubmit={handleSubmit(onSubmit)}>
        <CardContent className={classes.cardContent}>
          {getAgenciesRes.loading && <PagCircularIndeterminate size={24} />}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                render={({ field, fieldState }) => (
                  <PagTextInput
                    label="client"
                    placeholder="client"
                    InputProps={{
                      readOnly: true,
                      disabled: true,
                    }}
                    size="small"
                    variant="outlined"
                    error={fieldState.error}
                    value={field.value}
                    onChange={(e: any) => field.onChange(e.target.value)}
                    onBlur={field.onBlur}
                  />
                )}
                control={control}
                name={`clientName`}
                defaultValue={client?.name || ''}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                render={({ field, fieldState, formState }) => (
                  <PagAutocomplete
                    {...{
                      name: 'brandId',
                      optionLabelKey: 'name',
                      optionValueKey: 'id',
                      options: brandOptions,
                      error: fieldState.error,
                      value: field.value || null,
                      isCreatable: false,
                      AutocompleteProps: {
                        getOptionLabel: (option: IBrand) => safeLabel(option?.name),
                        // getOptionSelected: (option: any, value: any) => option.value === field.value,
                        size: 'small',
                        disabled: Boolean(brand),
                        disableClearable: true,
                        onChange: (event: object, values: IBrand, reason: string) => {
                          field.onChange(values ? values.id : null)
                        },
                        onBlur: field.onBlur,
                      },
                      InputProps: {
                        variant: 'outlined',
                        label: 'Select Brand',
                        placeholder: 'Select Brand',
                      },
                    }}
                  />
                )}
                control={control}
                name="brandId"
                defaultValue={0}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                render={({ field, fieldState, formState }) => (
                  <PagAutocomplete
                    {...{
                      name: 'agencyId',
                      optionLabelKey: 'name',
                      optionValueKey: 'id',
                      options: agencyOptions,
                      error: fieldState.error,
                      value: field.value || null,
                      isCreatable: false,
                      AutocompleteProps: {
                        getOptionLabel: (option: IAgency) => safeLabel(option?.name),
                        // getOptionSelected: (option: any, value: any) => option.value === field.value,
                        size: 'small',
                        disableClearable: false,
                        onChange: (event: object, values: IAgency, reason: string) => {
                          field.onChange(values ? values.id : null)
                        },
                        onBlur: field.onBlur,
                      },
                      InputProps: {
                        variant: 'outlined',
                        label: 'Select Agency',
                        placeholder: 'Select Agency',
                      },
                    }}
                  />
                )}
                control={control}
                name="agencyId"
                defaultValue={0}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                render={({ field, fieldState }) => (
                  <PagTextInput
                    label="Project Title"
                    placeholder="Project Title"
                    size="small"
                    variant="outlined"
                    error={fieldState.error}
                    value={field.value}
                    onChange={(e: any) => field.onChange(e.target.value || '')}
                    onBlur={field.onBlur}
                  />
                )}
                control={control}
                name={`projectName`}
                defaultValue={''}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                render={({ field, fieldState }) => (
                  <PagTextInput
                    label="Job Number"
                    placeholder="Job Number"
                    size="small"
                    variant="outlined"
                    error={fieldState.error}
                    value={field.value}
                    onChange={(e: any) => field.onChange(e.target.value || '')}
                    onBlur={field.onBlur}
                  />
                )}
                control={control}
                name={`projectNumber`}
                defaultValue={''}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="body2"
                className="pag-blue"
                onClick={() => setShowFileUpload(!showFileUpload)}
                style={{ cursor: 'pointer', marginTop: 8 }}
              >
                {showFileUpload ? 'Hide File Upload' : 'Upload Documents (Optional)'}
              </Typography>
            </Grid>
            {showFileUpload && (
              <>
                <Grid item xs={12}>
                  <div
                    className={classes.dropZoneWrapper}
                    {...getRootProps()}
                    onClick={(e) => {
                      e.stopPropagation()
                      open()
                    }}
                  >
                    <input {...getInputProps()} />
                    {isDragActive ? (
                      <Typography variant="body2">Drop files here to upload</Typography>
                    ) : (
                      <div>
                        <Typography variant="body2" style={{ fontSize: 14 }}>
                          Drag and drop files here or{' '}
                          <span className="pag-blue" style={{ cursor: 'pointer' }}>
                            click to browse
                          </span>
                        </Typography>
                      </div>
                    )}
                  </div>
                  {uploadingProgressRef.current.uploading && (
                    <Box display="flex" alignItems="center" style={{ marginTop: 8, width: '100%' }}>
                      <Box width="100%" mr={1}>
                        <LinearProgress
                          variant="determinate"
                          value={uploadingProgressRef.current.value}
                        />
                      </Box>
                      <Box minWidth={35}>
                        <Typography variant="body2" color="textSecondary">
                          {`${Math.round(uploadingProgressRef.current.value)}%`}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Grid>
                {uploadedFilesRef.current.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">
                      Uploaded Files:
                    </Typography>
                    <List dense className={classes.fileList}>
                      {uploadedFilesRef.current.map((f: any, i: number) => (
                        <ListItem key={`uploaded-file-${i}`} dense disableGutters>
                          <ListItemIcon>
                            <FontAwesomeIcon
                              icon={FileIcons[f.mimetype] || FileIcons[f.type] || DefaultFileIcon}
                              size="sm"
                            />
                          </ListItemIcon>
                          <ListItemText primary={f.filename || f.name} />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </CardContent>
        <CardActions className={classes.cardAction}>
          <Button
            color="primary"
            size="medium"
            aria-label="cancel"
            variant="text"
            className={classes.button}
            disabled={addProjectRes.loading || updateProjectRes.loading}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          &nbsp;&nbsp;&nbsp;
          <Button
            color="primary"
            size="medium"
            type="submit"
            aria-label="next"
            variant="contained"
            className={classes.button}
            disabled={
              !isValid ||
              addProjectRes.loading ||
              updateProjectRes.loading ||
              uploadProjectWithDocumentsRes.loading ||
              uploadingProgressRef.current.uploading
            }
          >
            Next
            {(addProjectRes.loading ||
              updateProjectRes.loading ||
              uploadProjectWithDocumentsRes.loading) && (
                <CircularProgress size={16} color="primary" className={classes.circularProgress} />
              )}
          </Button>
        </CardActions>
      </form>
    </Card>
  )
}

type UploadMirrorStep = 'search' | 'details' | 'upload'

const UploadMirrorProjectEditWidget = (props: any) => {
  const {
    inputs: { client },
    outputs: { handleCancel, handleSubmit: onHandleSubmit },
  } = props

  const classes = useStyles(props)
  const { store } = useContext(Context)
  const { activeTenant } = store
  const { getTokenSilently } = useAuth0()
  const mountedRef = useMounted(true)

  const [step, setStep] = useState<UploadMirrorStep>('search')
  const [projectNumber, setProjectNumber] = useState<string>('')
  const [project, setProject] = useState<IProject | null>(null)
  const [agencyOptions, setAgencyOptions] = useState<any[]>([])
  const [uploadPortalSelectHydration, setUploadPortalSelectHydration] = useState<UploadPortalSelectHydration>({})
  const apolloClient = useApolloClient()

  const [getAgencies] = useLazyQuery(GET_AGENCIES, {
    onError: (err: any) => {
      if (!mountedRef.current) return
      setAgencyOptions([])
      errorHandler(err)
    },
    onCompleted: (res: any) => {
      if (Boolean(mountedRef.current)) {
        setAgencyOptions(orderAgencies(res.agencies?.data || []))
      }
    },
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    if (!activeTenant?.id) return
    const abortController = new AbortController()
    const { signal } = abortController
    callPagApi('', getTokenSilently, getAgencies, { tenantId: activeTenant.id }, signal)
    return () => {
      if (abortController) abortController.abort()
    }
  }, [activeTenant?.id, getTokenSilently, getAgencies])

  useEffect(() => {
    if (!client?.id || !activeTenant?.id) {
      setUploadPortalSelectHydration({})
      return
    }
    const needed = getUploadPortalSelectFieldsToHydrate(client)
    needed.delete('brandId')
    needed.delete('agencyId')
    if (needed.size === 0) {
      setUploadPortalSelectHydration({})
      return
    }

    const tid = activeTenant.id
    const cid = client.id
    let cancelled = false

    const run = async () => {
      // GraphQL permission checks on the server rely on Auth0 bearer token.
      // `callPagApi()` injects it automatically elsewhere; here we must attach it via Apollo `context`.
      const token = await getTokenSilently()
      const authContext = { headers: { Authorization: `Bearer ${token}` } }
      const lists: UploadPortalSelectHydration = {}
      const tasks: Promise<void>[] = []

      if (needed.has('projectTypeId')) {
        tasks.push(
          apolloClient
            .query({
              query: GET_PROJECT_TYPES,
              variables: { tenantId: tid },
              fetchPolicy: 'network-only',
              context: authContext,
            })
            .then(({ data }) => {
              lists.projectTypes = orderProjectTypes(data?.projectTypes?.data || [])
            })
        )
      }
      if (needed.has('bidTypeId')) {
        tasks.push(
          apolloClient
            .query({
              query: GET_BID_TYPES,
              variables: { tenantId: tid },
              fetchPolicy: 'network-only',
              context: authContext,
            })
            .then(({ data }) => {
              lists.bidTypes = orderBidTypes(data?.bidTypes?.data || [])
            })
        )
      }
      if (needed.has('projectCategoryId')) {
        tasks.push(
          apolloClient
            .query({
              query: GET_PROJECT_CATEGORIES,
              variables: { tenantId: tid },
              fetchPolicy: 'network-only',
              context: authContext,
            })
            .then(({ data }) => {
              lists.projectCategories = orderProjectCategories(data?.projectCategories?.data || [])
            })
        )
      }
      if (needed.has('projectRegionId')) {
        tasks.push(
          apolloClient
            .query({
              query: GET_PROJECT_REGIONS,
              variables: { tenantId: tid },
              fetchPolicy: 'network-only',
              context: authContext,
            })
            .then(({ data }) => {
              lists.projectRegions = orderProjectRegions(data?.projectRegions?.data || [])
            })
        )
      }
      if (needed.has('projectStatusId')) {
        tasks.push(
          apolloClient
            .query({
              query: GET_PROJECT_STATUSES,
              variables: { tenantId: tid },
              fetchPolicy: 'network-only',
              context: authContext,
            })
            .then(({ data }) => {
              lists.projectStatuses = orderProjectStatuses(data?.projectStatuses?.data || [])
            })
        )
      }
      if (needed.has('campaignId')) {
        tasks.push(
          apolloClient
            .query({
              query: GET_CAMPAIGNS,
              variables: { tenantId: tid, clientId: cid },
              fetchPolicy: 'network-only',
              context: authContext,
            })
            .then(({ data }) => {
              lists.campaigns = orderCampaigns(data?.campaigns?.data || [])
            })
        )
      }
      if (needed.has('marketId')) {
        tasks.push(
          apolloClient
            .query({
              query: GET_MARKETS,
              variables: { tenantId: tid, clientId: cid },
              fetchPolicy: 'network-only',
              context: authContext,
            })
            .then(({ data }) => {
              lists.markets = orderMarkets(data?.markets?.data || [])
            })
        )
      }
      if (needed.has('agencyProducerId')) {
        tasks.push(
          apolloClient
            .query({
              query: GET_AGENCY_PRODUCERS,
              variables: { tenantId: tid },
              fetchPolicy: 'network-only',
              context: authContext,
            })
            .then(({ data }) => {
              lists.agencyProducers = orderAgencyProducers(data?.agencyProducers?.data || [])
            })
        )
      }

      try {
        await Promise.all(tasks)
      } catch (e) {
        if (mountedRef.current) errorHandler(e)
        return
      }
      if (!cancelled && mountedRef.current) {
        setUploadPortalSelectHydration(lists)
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [apolloClient, client, activeTenant?.id, getTokenSilently])

  const uploadClient = useMemo(() => {
    if (!client) return null
    const tenantId = client.tenantId ?? activeTenant?.id
    const withTenant = { ...client, tenantId, tenant: activeTenant }
    return injectUploadPortalSelectOptions(withTenant, {
      ...uploadPortalSelectHydration,
      agencies: agencyOptions,
    })
  }, [client, activeTenant, agencyOptions, uploadPortalSelectHydration])

  const handleCancelInternal = () => {
    setStep('search')
    setProjectNumber('')
    setProject(null)
    handleCancel?.()
  }

  const panel = (
    <Card id="upload-project-widget" className={clsx(classes.uploadProjectCard)} elevation={24} variant="outlined">
      <CardContent className={classes.uploadProjectCardContent}>
        {step === 'search' && uploadClient && (
          <UploadPortalSearchProjectForm
            isInAppUpload
            client={uploadClient}
            projectNumber={projectNumber}
            handleCancel={handleCancelInternal}
            handleNext={(e: { projectNumber: string; project: IProject | null }) => {
              setProjectNumber(e.projectNumber)
              setProject(e.project)
              setStep(e.project ? 'upload' : 'details')
            }}
          />
        )}

        {step === 'details' && uploadClient && (
          <UploadPortalNewProjectForm
            isInAppUpload
            client={uploadClient}
            project={project}
            projectNumber={projectNumber}
            handleChangeProjectNumber={(e: string) => {
              setProjectNumber(e)
              setProject(null)
              setStep('search')
            }}
            handleCancel={handleCancelInternal}
            handleNext={(e: IProject) => {
              setProject(e)
              setStep('upload')
            }}
            handleBack={() => {
              setStep('search')
            }}
          />
        )}

        {step === 'upload' && uploadClient && project && (
          <UploadPortalUploadFilesForm
            client={uploadClient}
            project={project}
            handleCancel={handleCancelInternal}
            handleBack={() => {
              if (project?.id) setStep('search')
              else setStep('details')
            }}
            handleStartNewUpload={() => {
              setProjectNumber('')
              setProject(null)
              setStep('search')
            }}
            onUploaded={(uploadedProject: IProject) => {
              onHandleSubmit(uploadedProject)
            }}
          />
        )}
      </CardContent>
    </Card>
  )

  if (typeof document === 'undefined') return null
  return createPortal(panel, document.body)
}

ProjectEditWidget.propTypes = {
  widgetConfig: WidgetConfigType,
  inputs: PropTypes.shape({
    client: PropTypes.object,
    brand: PropTypes.object,
    project: PropTypes.object,
  }).isRequired,
  outputs: PropTypes.shape({
    handleCancel: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
  }).isRequired,
}

ProjectEditWidget.defaultProps = {
  widgetConfig: WidgetConfigDefaultProps,
  outputs: {
    handleSubmit: (e: any) => { },
  },
}

export default UploadMirrorProjectEditWidget